import type {
  IFilterOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IZtnaNetworkLogData } from "./types";

import type { IBackFilters } from "../types";
import { filterDataset } from "../utils";
import { useFiltersStorage } from "storage/filters";
import { getDateRangeFilter } from "utils/filters";

const useOrgNetworkLogsFilters = (
  networkLogs: IZtnaNetworkLogData[],
  setBackFilters: React.Dispatch<React.SetStateAction<IBackFilters>>,
  setFilteredDataset: React.Dispatch<
    React.SetStateAction<IZtnaNetworkLogData[]>
  >,
): IUseFilterProps<IZtnaNetworkLogData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const optionsProps = useMemo(
    (): IOptionsProps<IZtnaNetworkLogData>[] => [
      {
        filterOptions: [
          {
            key: "date",
            label: t("organization.tabs.ztna.tabs.network.date"),
            minValue: dayjs().subtract(1, "days").format("YYYY-MM-DD"),
            type: "dateRange",
          },
        ],
        label: t("organization.tabs.ztna.tabs.network.date"),
      },
      {
        filterOptions: [
          {
            key: "email",
            label: t("organization.tabs.ztna.tabs.network.email"),
            type: "text",
          },
        ],
        label: t("organization.tabs.ztna.tabs.network.email"),
      },
    ],

    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("ztna_logs_network") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("ztna_logs_network", value);
    },
    [setValues],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IZtnaNetworkLogData>[]): void => {
      const newFiltersArray =
        newFilters as IFilterOptions<IZtnaNetworkLogData>[];
      const { from, to } = getDateRangeFilter(newFiltersArray, "date");
      const newBackFilters = { endDate: to, startDate: from ?? "" };

      setBackFilters(newBackFilters);
      setFilteredDataset(filterDataset(networkLogs, newFiltersArray));
    },
    [setBackFilters, setFilteredDataset, networkLogs],
  );

  return useFilters({
    dataset: networkLogs,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options: optionsProps,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useOrgNetworkLogsFilters };
